import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, MapPin, Building2, Users, Plus, Edit, RefreshCw } from 'lucide-react';
import ServiceLocationProfile from '@/components/admin/ServiceLocationProfile';

const ServiceLocationManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [profileId, setProfileId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_id: '',
    location_name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    industry: 'Other',
    total_sq_ft: '',
    employee_assigned: '',
    contact_number: '',
    assigned_as_of: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterLocations();
  }, [searchTerm, selectedIndustry, selectedCustomer, locations]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [locationsRes, statsRes] = await Promise.all([
        axios.get(`${API}/service-locations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/service-locations/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setLocations(locationsRes.data);
      setFilteredLocations(locationsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      const code = error?.response?.status;
      setLoadError(
        code
          ? `Unable to load service locations (server error ${code}). This is a system error, not an empty list — please retry.`
          : 'Unable to load service locations (network error). Please retry.'
      );
      setLocations([]);
      setFilteredLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const filterLocations = () => {
    let filtered = locations;

    if (searchTerm) {
      filtered = filtered.filter(loc =>
        (loc.location_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (loc.city || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (loc.address || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedIndustry) {
      filtered = filtered.filter(loc => loc.industry === selectedIndustry);
    }

    if (selectedCustomer) {
      filtered = filtered.filter(loc => loc.customer_name === selectedCustomer);
    }

    setFilteredLocations(filtered);
  };

  const uniqueIndustries = [...new Set(locations.map(l => l.industry))].sort();
  const uniqueCustomers = [...new Set(locations.map(l => l.customer_name))].sort();

  const handleAddLocation = () => {
    setFormData({
      customer_id: '',
      location_name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      industry: 'Other',
      total_sq_ft: '',
      employee_assigned: '',
      contact_number: '',
      assigned_as_of: '',
      notes: ''
    });
    setShowAddModal(true);
  };

  const handleEditLocation = (location) => {
    setFormData({
      customer_id: location.customer_id || '',
      customer_name: location.customer_name || '',
      location_name: location.location_name || '',
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zip: location.zip || '',
      county: location.county || '',
      industry: location.industry || 'Other',
      total_sq_ft: location.total_sq_ft || '',
      employee_assigned: location.employee_assigned || '',
      contact_number: location.contact_number || '',
      assigned_as_of: location.assigned_as_of || '',
      notes: location.notes || ''
    });
    setEditingLocation(location);
    setShowEditModal(true);
  };

  const handleSubmitLocation = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        // Build update payload - only include fields that have values
        const updatePayload = {};
        
        // Helper to add field only if it has a value (not empty string, null, or undefined)
        const addField = (key, value) => {
          if (value !== null && value !== undefined && value !== '') {
            updatePayload[key] = value;
          }
        };
        
        addField('customer_id', formData.customer_id);
        addField('customer_name', formData.customer_name);
        addField('location_name', formData.location_name);
        addField('address', formData.address);
        addField('city', formData.city);
        addField('state', formData.state);
        addField('zip', formData.zip);
        addField('county', formData.county);
        addField('industry', formData.industry);
        addField('employee_assigned', formData.employee_assigned);
        addField('contact_number', formData.contact_number);
        addField('assigned_as_of', formData.assigned_as_of);
        addField('notes', formData.notes);
        
        // Handle numeric fields separately
        if (formData.total_sq_ft) {
          updatePayload.total_sq_ft = parseFloat(formData.total_sq_ft);
        }
        
        await axios.put(`${API}/service-locations/${editingLocation.id}`, updatePayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service location updated successfully');
        setShowEditModal(false);
        setEditingLocation(null);
      } else {
        await axios.post(`${API}/service-locations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service location created successfully');
        setShowAddModal(false);
      }
      fetchData();
    } catch (error) {
      console.error('Submit error:', error.response?.data || error);
      toast.error(`Failed to ${editingLocation ? 'update' : 'create'} service location: ${error.response?.data?.detail || error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {profileId && <ServiceLocationProfile locationId={profileId} onClose={() => setProfileId(null)} />}
      
      
      <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Service Locations</h2>
          <p className="text-gray-600 mt-1">Manage TFS service location master dataset</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleAddLocation} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add New Service Location
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Locations</p>
                <p className="text-3xl font-bold">{stats?.total_locations || 0}</p>
              </div>
              <MapPin className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sq. Ft.</p>
                <p className="text-2xl font-bold">
                  {stats?.total_square_footage ? 
                    `${(stats.total_square_footage / 1000).toFixed(0)}K` : '0'}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Customers</p>
                <p className="text-3xl font-bold">{uniqueCustomers.length}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Industries</p>
                <p className="text-3xl font-bold">{uniqueIndustries.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by location, city, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <select
              className="border rounded-md px-3 py-2"
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
            >
              <option value="">All Industries</option>
              {uniqueIndustries.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>

            <select
              className="border rounded-md px-3 py-2"
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
            >
              <option value="">All Customers</option>
              {uniqueCustomers.map(customer => (
                <option key={customer} value={customer}>{customer}</option>
              ))}
            </select>
          </div>

          {(searchTerm || selectedIndustry || selectedCustomer) && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {filteredLocations.length} of {locations.length} locations
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedIndustry('');
                  setSelectedCustomer('');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Locations Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Service Locations</CardTitle>
        </CardHeader>
        <CardContent>
          {loadError && (
            <div data-testid="service-locations-error" className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
              <span>{loadError}</span>
              <Button variant="outline" size="sm" data-testid="service-locations-retry" onClick={fetchData}>Retry</Button>
            </div>
          )}
          <div className="border rounded-md">
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b">
                    <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap text-xs" style={{width: '110px', maxWidth: '110px'}}>Customer</th>
                    <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap text-xs" style={{width: '120px', maxWidth: '120px'}}>Location</th>
                    <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap text-xs" style={{width: '130px', maxWidth: '130px'}}>Address</th>
                    <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap text-xs" style={{width: '85px', maxWidth: '85px'}}>City, ST</th>
                    <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap text-xs" style={{width: '55px', maxWidth: '55px'}}>ZIP</th>
                    <th className="text-right py-1.5 px-1 font-medium whitespace-nowrap text-xs" style={{width: '50px', maxWidth: '50px'}}>Sq Ft</th>
                    <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap text-xs" style={{width: '80px', maxWidth: '80px'}}>Assigned</th>
                    <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap text-xs" style={{width: '70px', maxWidth: '70px'}}>Date</th>
                    <th className="text-right py-1.5 px-1 font-medium whitespace-nowrap sticky right-0 bg-gray-50 shadow-[-2px_0_4px_rgba(0,0,0,0.05)] z-20 text-xs" style={{width: '85px', maxWidth: '85px'}}>Actions</th>
                  </tr>
                </thead>
              <tbody>
                {filteredLocations.map((location) => (
                  <tr key={location.id} className="border-b hover:bg-gray-50">
                    <td className="py-1 px-0.5 font-medium truncate text-xs" title={location.customer_name} style={{width: '110px', maxWidth: '110px'}}>
                      {location.customer_name}
                    </td>
                    <td className="py-1 px-0.5 truncate text-xs" title={location.location_name} style={{width: '120px', maxWidth: '120px'}}>
                      <button onClick={() => setProfileId(location.id)} data-testid={`location-open-${location.id}`} className="text-left font-semibold text-[#1763B6] hover:underline">{location.location_name}</button>
                    </td>
                    <td className="py-1 px-0.5 text-gray-600 truncate text-xs" title={location.address} style={{width: '130px', maxWidth: '130px'}}>
                      {location.address}
                    </td>
                    <td className="py-1 px-0.5 truncate text-xs" title={`${location.city}, ${location.state}`} style={{width: '85px', maxWidth: '85px'}}>
                      {location.city}, {location.state}
                    </td>
                    <td className="py-1 px-0.5 text-xs" title={location.zip} style={{width: '55px', maxWidth: '55px'}}>
                      {location.zip || '-'}
                    </td>
                    <td className="py-1 px-0.5 text-right text-xs" style={{width: '50px', maxWidth: '50px'}}>
                      {location.total_sq_ft ? 
                        `${(location.total_sq_ft / 1000).toFixed(0)}k` : '-'}
                    </td>
                    <td className="py-1 px-0.5 text-gray-600 truncate text-xs" style={{width: '80px', maxWidth: '80px'}}>
                      {location.employee_assigned || '-'}
                    </td>
                    <td className="py-1 px-0.5 text-gray-600 text-xs" style={{width: '70px', maxWidth: '70px'}}>
                      {location.assigned_as_of ? _fmtDate(location.assigned_as_of) : '-'}
                    </td>
                    <td className="py-1 px-0.5 sticky right-0 bg-white shadow-[-2px_0_4px_rgba(0,0,0,0.05)] z-20" style={{width: '85px', maxWidth: '85px'}}>
                      <div className="flex gap-0.5 justify-end">
                        <button
                          onClick={() => setProfileId(location.id)}
                          title="Open Profile"
                          data-testid={`location-profile-btn-${location.id}`}
                          className="h-6 w-6 p-0 inline-flex items-center justify-center rounded hover:bg-blue-100 transition-colors"
                        >
                          <Building2 className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleEditLocation(location)}
                          title="Edit"
                          className="h-6 w-6 p-0 inline-flex items-center justify-center rounded hover:bg-amber-100 transition-colors"
                        >
                          <Edit className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

            {filteredLocations.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No locations found matching your filters
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Service Location Modal */}
      <Dialog open={showAddModal || showEditModal} onOpenChange={() => {
        setShowAddModal(false);
        setShowEditModal(false);
        setEditingLocation(null);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? 'Edit Service Location' : 'Add New Service Location'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitLocation}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customer_id">Customer</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({...formData, customer_id: value})}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="location_name">Location Name</Label>
                <Input
                  id="location_name"
                  value={formData.location_name}
                  onChange={(e) => setFormData({...formData, location_name: e.target.value})}
                  required
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  value={formData.zip}
                  onChange={(e) => setFormData({...formData, zip: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="industry">Industry</Label>
                <Select
                  value={formData.industry}
                  onValueChange={(value) => setFormData({...formData, industry: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Education">Education</SelectItem>
                    <SelectItem value="Banking">Banking</SelectItem>
                    <SelectItem value="Religious Institution">Religious Institution</SelectItem>
                    <SelectItem value="Office">Office</SelectItem>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Healthcare">Healthcare</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="total_sq_ft">Total Square Feet</Label>
                <Input
                  id="total_sq_ft"
                  type="number"
                  value={formData.total_sq_ft}
                  onChange={(e) => setFormData({...formData, total_sq_ft: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="employee_assigned">Assigned Personnel</Label>
                <Input
                  id="employee_assigned"
                  value={formData.employee_assigned}
                  onChange={(e) => setFormData({...formData, employee_assigned: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="assigned_as_of">Assigned As Of</Label>
                <Input
                  id="assigned_as_of"
                  type="date"
                  value={formData.assigned_as_of}
                  onChange={(e) => setFormData({...formData, assigned_as_of: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="contact_number">Contact Number</Label>
                <Input
                  id="contact_number"
                  value={formData.contact_number}
                  onChange={(e) => setFormData({...formData, contact_number: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
                setEditingLocation(null);
              }}>
                Cancel
              </Button>
              <Button type="submit">
                {editingLocation ? 'Update Location' : 'Add Location'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default ServiceLocationManagement;
